import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'

export const useTabsStore = defineStore('tabs', () => {
  const router = useRouter()
  
  // 状态
  const tabs = ref([])
  const activeTab = ref('')

  // 计算属性
  const currentTab = computed(() => {
    return tabs.value.find(tab => tab.path === activeTab.value)
  })

  // 标签页元数据配置
  const getTabMeta = (route) => {
    const tabMetas = {
      '/dashboard': {
        title: '快捷面板',
        icon: 'HomeIcon',
        closable: false // 快捷面板不可关闭
      },
      '/customers': {
        title: '客户管理',
        icon: 'UsersIcon',
        closable: true
      },
      '/email-accounts': {
        title: '邮件账户',
        icon: 'EnvelopeIcon',
        closable: true
      },
      '/emails': {
        title: '邮件管理',
        icon: 'InboxIcon',
        closable: true
      },
      '/campaigns': {
        title: '邮件营销',
        icon: 'MegaphoneIcon',
        closable: true
      },
      '/templates': {
        title: '邮件模板',
        icon: 'DocumentTextIcon',
        closable: true
      },
      '/products': {
        title: '产品管理',
        icon: 'ShoppingBagIcon',
        closable: true
      },
      '/external-orders': {
        title: '外部订单',
        icon: 'ShoppingBagIcon',
        closable: true
      },
      '/qr-code': {
        title: '售后二维码',
        icon: 'QrCodeIcon',
        closable: true
      },
      '/qr-code-pro': {
        title: '售后二维码',
        badge: 'PRO',
        icon: 'QrCodeIcon',
        closable: true
      },
      '/brand-site': {
        title: '品牌独立站',
        icon: 'BuildingStorefrontIcon',
        closable: true
      },
      '/user-management': {
        title: '用户管理',
        icon: 'UserGroupIcon',
        closable: true
      },
      '/profile': {
        title: '个人资料',
        icon: 'UserIcon',
        closable: true
      },
      '/sensitive-words': {
        title: '敏感词库',
        icon: 'ShieldExclamationIcon',
        closable: true
      },
      '/async-email-tasks': {
        title: '异步任务',
        icon: 'ClockIcon',
        closable: true
      }
    }

    // 处理动态路由（如客户详情页）
    if (route.path.startsWith('/customers/') && route.params.id) {
      return {
        title: `客户详情`,
        icon: 'UserIcon',
        closable: true
      }
    }

    return tabMetas[route.path] || {
      title: route.meta?.title || '未知页面',
      icon: 'DocumentIcon',
      closable: true
    }
  }

  // 添加标签页
  const addTab = (route) => {
    const existingTab = tabs.value.find(tab => tab.path === route.path)
    
    if (!existingTab) {
      const tabMeta = getTabMeta(route)
      const newTab = {
        path: route.path,
        name: route.name,
        title: tabMeta.title,
        badge: tabMeta.badge || null,
        icon: tabMeta.icon,
        closable: tabMeta.closable,
        params: route.params,
        query: route.query,
        timestamp: Date.now(),
        refreshKey: Date.now() // 添加刷新key
      }
      
      tabs.value.push(newTab)
    } else {
      // 确保已存在的标签页也有refreshKey
      if (!existingTab.refreshKey) {
        existingTab.refreshKey = Date.now()
      }
    }
    
    activeTab.value = route.path
  }

  // 关闭标签页
  const closeTab = (targetPath) => {
    const targetIndex = tabs.value.findIndex(tab => tab.path === targetPath)
    
    if (targetIndex === -1) return
    
    const targetTab = tabs.value[targetIndex]
    
    // 如果是不可关闭的标签页，直接返回
    if (!targetTab.closable) return

    // 如果关闭的是当前活跃标签页
    if (targetPath === activeTab.value) {
      let nextTab
      
      // 优先选择右侧的标签页，如果没有则选择左侧的
      if (targetIndex < tabs.value.length - 1) {
        nextTab = tabs.value[targetIndex + 1]
      } else if (targetIndex > 0) {
        nextTab = tabs.value[targetIndex - 1]
      }
      
      if (nextTab) {
        activeTab.value = nextTab.path
        // iframe模式下不需要路由跳转
      } else {
        // 如果没有其他标签页，设置为空
        activeTab.value = ''
      }
    }
    
    // 移除标签页
    tabs.value.splice(targetIndex, 1)
  }

  // 关闭其他标签页
  const closeOtherTabs = (keepPath) => {
    tabs.value = tabs.value.filter(tab => 
      tab.path === keepPath || !tab.closable
    )
    
    if (activeTab.value !== keepPath) {
      activeTab.value = keepPath
      // iframe模式下不需要路由跳转
    }
  }

  // 关闭所有可关闭的标签页
  const closeAllTabs = () => {
    tabs.value = tabs.value.filter(tab => !tab.closable)
    
    // 切换到第一个不可关闭的标签页（通常是仪表板）
    const firstUnclosableTab = tabs.value[0]
    if (firstUnclosableTab) {
      activeTab.value = firstUnclosableTab.path
      // iframe模式下不需要路由跳转
    }
  }

  // 切换到指定标签页（iframe模式不需要路由跳转）
  const switchToTab = (targetPath) => {
    const targetTab = tabs.value.find(tab => tab.path === targetPath)
    
    if (targetTab) {
      activeTab.value = targetPath
      // iframe模式下不进行路由跳转，只切换显示状态
    }
  }

  // 刷新指定标签页
  const refreshTab = (targetPath) => {
    const targetTab = tabs.value.find(tab => tab.path === targetPath)
    
    if (targetTab) {
      // 更新refreshKey以触发组件重新渲染
      targetTab.refreshKey = Date.now()
    }
  }

  // 初始化标签页（添加仪表板作为默认标签页）
  const initTabs = () => {
    if (tabs.value.length === 0) {
      addTab({
        path: '/dashboard',
        name: 'Dashboard',
        params: {},
        query: {}
      })
    }
  }

  // 刷新当前标签页
  const refreshCurrentTab = () => {
    if (currentTab.value) {
      router.go(0) // 刷新页面
    }
  }

  // 判断标签页是否存在
  const hasTab = (path) => {
    return tabs.value.some(tab => tab.path === path)
  }

  return {
    // 状态
    tabs,
    activeTab,
    
    // 计算属性
    currentTab,
    
    // 方法
    addTab,
    closeTab,
    closeOtherTabs,
    closeAllTabs,
    switchToTab,
    refreshTab,
    initTabs,
    refreshCurrentTab,
    hasTab
  }
})
